import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';


/* ─────────────────────────────────────────────────────────────
   ERPForce PDF Builder — Stationery LIBRARY store (shared)
   A client can keep SEVERAL letterhead/footer presets (e.g. a
   standard masthead, a bilingual split, a minimal mono one).
   Both the Stationery Master editor and the PDF Builder read from
   this one library so a document can pick whichever it needs —
   or none at all (Blank / Pre-printed).

   Shape:  { presets: [ Preset, … ], activeId }
   Preset: { id, name, mode, brand, header, footer, watermark, reserve }
   Saved under 'erpforce-stationery-library-v1'. Migrates the old
   single-master key automatically.  Exposes STStore.
   ───────────────────────────────────────────────────────────── */

const LIB = 'altrad-stationery-library-v2';
const OLD = 'erpforce-stationery-master-v1';   // legacy single master

const uid = () => 'st-' + Math.random().toString(36).slice(2, 9);

// ── field defaults (kept in sync with the editor) ───────────
const baseHeader = () => ({
  show: true, variant: 'standard', showLogo: true, logoSize: 46, logoSrc: null,
  nameEN: '', nameAR: '',
  bilingual: false, tagline: '', rule: false, repeat: 'all', compactCont: true,
});
const baseFooter = () => ({
  show: true, regBand: false,
  regItems: [],
  contactRow: true, address: '', phone: '', email: '',
  pageNumbers: false, format: 'Page {n} of {total}', note: '', repeat: 'all',
});
const baseWatermark = () => ({ show: true, opacity: 6, size: 56, rotation: 0 });
const baseReserve = () => ({ top: 156, bottom: 120 });

const defPreset = (over = {}) => ({
  id: uid(), name: 'Untitled stationery', mode: 'letterhead', brand: '#DD0D1A',
  paper: 'A4', orient: 'portrait',
  header: baseHeader(), footer: baseFooter(), watermark: baseWatermark(), reserve: baseReserve(),
  sections: [],
  ...over,
});

// defensively fill any missing sub-keys (forward-compatible loads)
const mergePreset = (p) => ({
  ...defPreset(), ...p,
  id: p.id || uid(),
  paper: p.paper || 'A4',
  orient: p.orient || 'portrait',
  header: { ...baseHeader(), ...(p.header || {}) },
  footer: { ...baseFooter(), ...(p.footer || {}) },
  watermark: { ...baseWatermark(), ...(p.watermark || {}) },
  reserve: { ...baseReserve(), ...(p.reserve || {}) },
  sections: Array.isArray(p.sections) ? p.sections : (
    // Migrate old customHeader/customFooter if they exist
    [
      ...(Array.isArray(p.customHeader) ? p.customHeader.map((s: any) => ({ ...s, zone: 'header' })) : []),
      ...(Array.isArray(p.customFooter) ? p.customFooter.map((s: any) => ({ ...s, zone: 'footer' })) : [])
    ]
  ),
});

const seedLib = () => {
  const p1 = defPreset({ name: 'Standard stationery', brand: '#14171b', header: { ...baseHeader(), variant: 'standard' } });
  return { presets: [p1], activeId: p1.id };
};

function load() {
  // Rely solely on backend persistence; return seeded data initially
  return seedLib();
}

function save(lib) {
  // No-op: Persistence is handled directly by API calls
}

const getActive = (lib) => (lib && lib.presets.find(p => p.id === lib.activeId)) || (lib && lib.presets[0]) || null;
const getById = (lib, id) => (lib && lib.presets.find(p => p.id === id)) || null;

export const STStore = { uid, defPreset, mergePreset, seedLib, load, save, getActive, getById };

